import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Which event keys are hidden, across every surface that renders domain events:
 * the map's cards and timeline dots, and the Cases/Aircraft Timeline lists. It
 * lives in `shared` so every consumer imports it downward. Keys are hidden, not
 * selected, so a type the BE starts emitting shows by default. The vocabulary
 * is whatever any consumer keys on (`aircraftTakeoff`, `organ_Heart`, `span_*`,
 * `phase_*`, `clinicalItem:*`); each consumer applies only the keys it
 * understands, so an unknown key is never "hide all".
 */

/**
 * Hidden on the Main Map by default and nowhere else. Organ keys only: the
 * map's family toggles never clear this list. A literal rather than
 * `organLegendKey('Kidney')`, so the slice imports nothing from `selectors.ts`;
 * the spec pins the two equal.
 */
export const MAP_DEFAULT_HIDDEN_EVENT_KEYS: readonly string[] = [
  'organ_Kidney',
];

/**
 * Not persisted: blacklisted beside `mapFocus`, because a rehydrated filter
 * opens the map with events missing and no way to tell why. The slice's
 * `mapHiddenKeys` is not persisted either, so kidney is hidden again after a
 * reload.
 */
export interface EventFilterState {
  /** Held as an array, not a Set: Redux state must stay serialisable. */
  hiddenKeys: string[];
  /** Hidden on the map's cards and strip only, on top of `hiddenKeys`. */
  mapHiddenKeys: string[];
}

const initialState: EventFilterState = {
  hiddenKeys: [],
  mapHiddenKeys: [...MAP_DEFAULT_HIDDEN_EVENT_KEYS],
};

export const eventFilterSlice = createSlice({
  name: 'events/filter',
  initialState,
  reducers: {
    eventKeyToggled: (state, { payload }: PayloadAction<string>) => {
      const at = state.hiddenKeys.indexOf(payload);

      if (at === -1) {
        state.hiddenKeys.push(payload);
      } else {
        state.hiddenKeys.splice(at, 1);
      }
    },
    // A family toggle expands to its member type keys, so both of these take a
    // batch. An empty batch is a no-op rather than a clear: a family the map
    // knows no types for must not read as "hide nothing, loudly".
    eventKeysHidden: (state, { payload }: PayloadAction<readonly string[]>) => {
      for (const key of payload) {
        if (!state.hiddenKeys.includes(key)) {
          state.hiddenKeys.push(key);
        }
      }
    },
    eventKeysShown: (state, { payload }: PayloadAction<readonly string[]>) => {
      if (payload.length === 0) {
        return;
      }

      const dropping = new Set(payload);
      state.hiddenKeys = state.hiddenKeys.filter(key => !dropping.has(key));
    },
    eventFilterReset: state => {
      state.hiddenKeys = [];
    },
    // The map's organ toggle reads the union the map shows. A key hidden only by
    // the map default is therefore shown in one click, not hidden a second time.
    // Hiding writes the shared list, so it hides the key on every surface, as the
    // map's family toggles do; only the default is map-only.
    mapEventKeyToggled: (state, { payload }: PayloadAction<string>) => {
      if (
        state.hiddenKeys.includes(payload) ||
        state.mapHiddenKeys.includes(payload)
      ) {
        state.hiddenKeys = state.hiddenKeys.filter(key => key !== payload);
        state.mapHiddenKeys = state.mapHiddenKeys.filter(
          key => key !== payload,
        );
      } else {
        state.hiddenKeys.push(payload);
      }
    },
    mapEventFilterReset: state => {
      state.hiddenKeys = [];
      state.mapHiddenKeys = [];
    },
  },
});

export const eventFilterActions = eventFilterSlice.actions;
